import { Injectable } from '@angular/core'
import { TranslocoService } from '@ngneat/transloco'
import format from 'date-fns/format'
import { NgxSpinnerService } from 'ngx-spinner'
import { firstValueFrom, Observable } from 'rxjs'
import { first } from 'rxjs/operators'
import { ApiService } from './api.service'
import { AuthService } from './auth.service'
import { CartType, ICartItem, ITransaction, TransactionType } from './cart.interface'
import { Cry } from './cry.service'
import { EmailService } from './email.service'
import { OrderStatus, PrimaryCode } from './enums'
import { FirestoreService2 } from './firestore.service2'
import { FnacService } from './fnac.service'
import { GlobalService } from './global.service'
import { NotifyService } from './notifications/notify.service'
import { RegionService } from './region.service'
import { Region, ResolvedService, TransportLine } from './shipping.interface'
import { ShippingService } from './shipping.service'
import { applySaleFees } from './sale-fees'
import { deepClonePreservingFieldValues, serializeTimestampsForApi } from './timestampUtils'
import { use } from './utils'

@Injectable({ providedIn: 'root' })
export class CartService {
  apiUrl: any
  private fss: FirestoreService2

  constructor(
    public notify: NotifyService,
    private spin: NgxSpinnerService,
    private auth: AuthService,
    public g: GlobalService,
    private fnac: FnacService,
    private transloco: TranslocoService,
    private regionService: RegionService,
    private shipping: ShippingService,
  ) {
    this.apiUrl = this.g.get('apiUrl')
  }

  add(what: string, product: any, user: any, cartLine?: any, cardIdTemp?: string) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)

    product = this.simplifyProduct(product)

    if (!product.isVoucher && this.g.get('selectedStore') && !this.g.get('selectedStore').userSelect) {
      this.g.set('openPostCodeModal', true)
    }
    if (!user[what] || !user[what].length) this.g.set('openPostCodeModal', true)

    const cardLineFreezed = Object.freeze(cartLine)
    const line = {
      ...product,
      ...cartLine,
      qty: product.qty || 1,
      vatRate: parseFloat(product.vatRate)
    }

    if (what === 'cartEuros') {
      line.priceEuros = parseFloat(product.priceEuros)
      line.amtEuros = line.priceEuros * (line.qty || 1)
      line.totalCartEuros = line.amtEuros

      const promotion = (this.g.program.maxOrderSubsidy / 100) * line.totalCartEuros
      line.totalCartEuros -= promotion
      line.totalCartEuros = parseFloat(line.totalCartEuros.toFixed(2))

    } else {
      line.price = parseFloat(product.price)
      line.points = parseFloat(product.points)
      line.amtPoints = parseFloat(product.amtPoints)
    }

    if (cartLine) {
      if (what === 'cartEuros') {
        line.priceEuros = parseFloat(cardLineFreezed.priceEuros)
        line.totalCartEuros = line.priceEuros * (line.qty || 1)
        line.amtEuros = parseFloat(cardLineFreezed.amtEuros)
      } else {
        line.points = parseFloat(cardLineFreezed.amtPoints)
        line.amtPoints = parseFloat(cardLineFreezed.amtPoints)
        line.price = line.points
      }

      line.vatRate = parseFloat(cardLineFreezed.vatRate)
      line.billable = cardLineFreezed.billable
      line['rechargeable'] = product.rechargeable
      line['seller'] = product.seller
      line['sellerId'] = product.sellerId
      line['sellerEmail'] = product.sellerEmail

      if (product.variablePrice || product.fixedPrices) {
        line.price = Math.ceil(line.price * this.g.program.cbXrate)
      }
      if (cardLineFreezed.cardId) {
        line['cardId'] = cardLineFreezed.cardId
      }

      if (cardIdTemp) line['cardIdTemp'] = cardIdTemp
    }

    if (!user[what]) user[what] = []
    if (what === 'wishlist' && user[what].find(i => i.id === line.id)) user[what].splice(user[what].findIndex(i => i.id === line.id), 1)
    else user[what].push({ ...line, addedInCart: new Date().toISOString() })
    user = this.calcUserTotals(user)

    this.fss.update(`users`, user.id, this.stripUiFields(user), { silent: true })

  }

  async validateSellerGatewayUid(sellerId: string): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)

    try {
      const seller = await firstValueFrom(this.fss.get('users', sellerId, { api: true }))

      if (!seller) {
        this.notify.alert(this.transloco.translate('error'), this.transloco.translate('seller_not_found'))
        return null
      }

      const sellerName = seller.displayName || seller.email

      if (!seller.gatewayUid) {
        this.notify.alert(this.transloco.translate('error'), this.transloco.translate('seller_payment_error_missing_uuid', { name: sellerName }))
        return null
      }

      return seller
    } catch (error) {
      return null
    }
  }

  private simplifyProduct(_product: any): any {
    const keep = [
      // core
      'id', 'name', 'qty', 'price', 'points', 'amtPoints', 'amtEuros',
      'vatRate', 'priceEuros', 'totalCartEuros', 'type', 'cartType', 'image',
      // card
      'cardId', 'cardIdTemp', 'cardReceivedIn', 'rechargeable', 'op',
      'initialLoad', 'deduct', 'priceMax', 'fee', 'mobile',
      // seller
      'seller', 'sellerId', 'sellerEmail', 'sellerCode',
      // stock
      'stock', 'manageStock', 'stockQty',
      // pricing
      'variablePrice', 'fixedPrices', 'originalPrice', 'originalPoints',
      'updatePriceDate', 'promotionBalance',
      // shipping
      'isTransport', 'deliveryPeriod', 'programOverrides', 'shippingCostEuros',
      'costEuros', 'costPoints', 'region', 'regionId', 'regionName',
      'serviceId', 'serviceName', 'deliveryDays', 'weight', 'volume',
      // display/UI
      'isVoucher', 'isDigital', 'pickup', 'yesCard', 'beevoId',
      'payable', 'customFields', 'billable', 'nonBillable',
      // variants
      'variants',
      // billing/cancel/credit (set after checkout, kept for updates)
      'reason', 'cancelQty', 'cancelAmt', 'cancelDate',
      'creditDate', 'creditQty', 'creditAmt', 'creditId',
      'shipDate', 'purchaseOrderNo', 'purchaseOrderDate',
      'purchaseQty', 'purchasePrice', 'purchaseVat', 'purchaseTtl',
      'purchaseInvoiceNo', 'purchaseInvoiceDate',
    ]

    const booleanDefaults: Record<string, boolean> = {
      rechargeable: false,
      stock: false,
      manageStock: false,
      variablePrice: false,
      fixedPrices: false,
      isTransport: false,
      isVoucher: false,
      isDigital: false,
      pickup: false,
      yesCard: false,
      payable: false,
      billable: false,
      nonBillable: false,
    }

    const out: any = {}
    for (const key of keep) {
      const val = _product[key]
      const isBoolField = key in booleanDefaults

      if (isBoolField) {
        out[key] = (val === true || val === false) ? val : booleanDefaults[key]
        continue
      }

      if (val === undefined || val === null) continue
      if (typeof val === 'string' && val === '') continue
      if (Array.isArray(val) && val.length === 0) continue
      if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) continue
      out[key] = val
    }
    return out
  }

  async modify(what: string, _product: any, user: any): Promise<boolean> {
    if (!_product.id && _product.objectID) _product.id = _product.objectID
    const product = this.simplifyProduct(_product)
    if (what === 'wishlist') { this.add(what, product, user); return true }
    else {
      if (what === 'cartEuros') {
        if (!product.sellerId) {
          this.notify.alert('Erro', this.transloco.translate('product_no_seller'))
          return false
        }

        // Validate seller gatewayUid before adding to cart
        const seller = await this.validateSellerGatewayUid(product.sellerId)
        if (!seller) return false

        const productData = await firstValueFrom(this.fss.get('products', product.id, { nick: this.g.nick }))

        if (productData) {
          if (product.manageStock) {
            const currentStockQty = productData.stockQty ?? 0

            if (currentStockQty <= 0) {
              this.notify.update(
                this.transloco.translate('stock_limit_exceeded'),
                'btn-danger',
                5000
              )
              return false
            }

            const currentItem = user.cartEuros?.find((item: any) => item.id === product.id)
            const currentQtyInCart = currentItem?.qty || 0

            if (currentQtyInCart + 1 > currentStockQty) {
              const message = this.transloco.translate('stock_limit_exceeded', {
                available: currentStockQty,
                inCart: currentQtyInCart
              })
              this.notify.update(message, 'btn-danger', 5000)
              return false
            }

            product.stockQty = currentStockQty
            product.manageStock = productData.manageStock ?? false
          }

          // Price check for all cartEuros products
          if (productData.priceEuros !== undefined && product.priceEuros !== undefined) {
            if (productData.priceEuros !== product.priceEuros) {
              this.notify.update(
                this.transloco.translate('checkout_out_of_stock_products'),
                'btn-warning',
                5000
              )
            }
            product.priceEuros = productData.priceEuros
          }
        }
      }

      const { isSame, index } = this.productIsSame(product, user, what)

      if (what === 'cart') user.pointsCart = []

      if (index > -1 && isSame) this.updateLine(what, 'plus', index, user)
      else this.add(what, product, user)

      this.notify.update(
        this.transloco.translate('added_to_cart'),
        'btn-success',
        3000
      )
    }
    return true
  }

  changeQtiesCart(sign: string, index: number, user: any, what: string) {
    const cartType = what

    if (user && user[cartType] && user[cartType][index]) {
      user.wishlist = user.wishlist || []

      if (sign === 'remove') user[cartType].splice(index, 1)
      else if (sign === 'plus' && user[cartType][index].rechargeable) this.notify.update(this.transloco.translate('card_one_per_line'), 'btn-danger', 10000)
      else if (sign === 'plus') {
        const item = user[cartType][index]

        // Check stock limit for manageStock items only
        if (item.manageStock && item.stockQty !== undefined && item.qty >= item.stockQty) {
          const message = this.transloco.translate('stock_limit_exceeded', {
            available: item.stockQty
          })
          this.notify.update(message, 'btn-danger', 5000)
          return user
        }

        user[cartType][index].qty++
      }
      else {
        user[cartType][index].qty--
        if (user[cartType][index].qty === 0) {
          const cardIdTemp = user[cartType][index].cardIdTemp
          if (cardIdTemp) {
            user[cartType] = [...user[cartType].filter(item => item.cardIdTemp !== cardIdTemp)]
          } else {
            user.wishlist.push({ ...user[cartType][index] })
            user[cartType].splice(index, 1)
          }
        }
      }
    }

    /**
     * Whenever an item has its value changed in the cart, the pointsCart must
     * be reset, so that a new purchase with recalculated points is
     * possible
     */
    user.pointsCart = []

    return { ...user }
  }

  changeQtiesWish(sign: string, index: number, user: any) {
    if (user && sign === 'minus') {
      if (sign === 'minus') {
        if (user.wishlist && user.wishlist[index]) {
          user.wishlist[index].qty--
          if (user.wishlist[index].qty <= 0) user.wishlist.splice(index, 1)
        }
      }
    }
    return { ...user }
  }

  calcUserTotals(user: any) {
    user.orderQty = 0
    user.wishQty = 0
    user.orderPoints = 0
    user.merchandisingPoints = 0
    user.wishlist = user.wishlist || []

    if (user.cart && user.cart instanceof Array) {
      user.cart = user.cart || []
      user.pointsCart = user.pointsCart || []

      for (const [index, cartItem] of user.cart.entries()) {
        cartItem.idx = index
        cartItem['points'] = parseFloat(cartItem['points'])
        cartItem['vatRate'] = parseFloat(cartItem['vatRate'])
        cartItem['billable'] = cartItem['billable']

        if (cartItem.id === 'buy-point') {
          cartItem.plus = '<span class="btn btn-circle btn-buy text-danger"><i class="fas fa-trash"></i></span>'
          cartItem.minus = ''
          cartItem.nameLink = cartItem.name
        } else if (cartItem['name'].indexOf('emissão') > -1/* && !this.g.program.options.chargeCardsToUsers*/) {
          cartItem.plus = ''
          cartItem.minus = ''
          cartItem.nameLink = cartItem.name
        } else {
          cartItem['amtEuros'] = cartItem['qty'] * cartItem['price']
          cartItem['amtPoints'] = cartItem['qty'] * cartItem['points']
          cartItem.plus = '<span class="btn btn-circle"><i class="fas fa-plus"></i></span>'
          cartItem.minus = '<span class="btn btn-circle"><i class="fas fa-minus"></i></span>'
          cartItem.nameLink = `<button class="btn btn-sm btn-link m-0 p-0">${cartItem.name}<i class="fas fa-link"></i></button>${!cartItem.stock ? ' <span class="badge badge-danger">' + this.transloco.translate('out_of_stock') + '</span>' : ''}`
          cartItem.nameMsg = `${cartItem.name}${!cartItem.stock ? ' <span class="badge badge-danger">' + this.transloco.translate('out_of_stock') + '</span>' : ''}`
        }

        user.orderQty += cartItem.qty
        user.orderPoints += cartItem.amtPoints
        user.orderEuros += cartItem.amtEuros
        if (cartItem['name'].indexOf(' - NOS') !== -1) user.merchandisingPoints += cartItem['amtPoints']
      }
    }

    if (user.cartEuros && user.cartEuros instanceof Array) {
      user.cartEuros = user.cartEuros || []
      user.orderEuros = 0

      for (const [index, cartItem] of user.cartEuros.entries()) {

        cartItem.idx = index
        cartItem.priceEuros = parseFloat(cartItem.priceEuros || 0)
        cartItem.amtEuros = cartItem.qty * cartItem.priceEuros
        cartItem.vatRate = parseFloat(cartItem.vatRate || 0)
        cartItem['billable'] = cartItem['billable']

        cartItem.totalCartEuros = cartItem.amtEuros - ((this.g.program.maxOrderSubsidy / 100) * cartItem.amtEuros)

        cartItem.plus = '<span class="btn btn-circle"><i class="fas fa-plus"></i></span>'
        cartItem.minus = '<span class="btn btn-circle"><i class="fas fa-minus"></i></span>'
        cartItem.nameLink = `<button class="btn btn-sm btn-link m-0 p-0">${cartItem.name}<i class="fas fa-link"></i></button>${!cartItem.stock ? ' <span class="badge badge-danger">' + this.transloco.translate('out_of_stock') + '</span>' : ''}`
        cartItem.nameMsg = `${cartItem.name}${!cartItem.stock ? ' <span class="badge badge-danger">' + this.transloco.translate('out_of_stock') + '</span>' : ''}`

        user.orderQty += cartItem.qty
        user.orderEuros += cartItem.amtEuros
      }
    }

    if (user.wishlist && user.wishlist instanceof Array) {
      for (const [index, whishItem] of user.wishlist.entries()) {
        whishItem.idx = index
        whishItem['points'] = parseFloat(whishItem['points'] || 0)
        whishItem['priceEuros'] = parseFloat(whishItem['priceEuros'] || 0)
        whishItem['vatRate'] = parseFloat(whishItem['vatRate'] || 0)
        whishItem['billable'] = whishItem['billable']

        if (whishItem['name'].indexOf('emissão') >= 0 && !this.g.program.options.chargeCardsToUsers) {
          whishItem['amtEuros'] = 0
          whishItem['amtPoints'] = 0
        } else {
          whishItem['amtEuros'] = whishItem['qty'] * (whishItem['price'] || whishItem['priceEuros'] || 0)
          whishItem['amtPoints'] = whishItem['qty'] * (whishItem['points'] || 0)
          whishItem['orderEuros'] = whishItem['qty'] * (whishItem['priceEuros'] || 0)

          whishItem.plus = '<span class="btn btn-circle"><i class="fas fa-shopping-cart"></i></span>'
          whishItem.minus = '<span class="btn btn-circle"><i class="fas fa-trash"></i></span>'
          whishItem.nameLink = '<button class="btn btn-sm btn-link m-0 p-0">' + whishItem.name + '<i class="fas fa-link"></i></button>'
        }

        user.wishQty += whishItem.qty
      }
    }

    this.spin.hide()
    return user
  }

  /**
   * Returns a shallow copy of user with UI-only fields (idx, plus, minus, nameLink, nameMsg)
   * stripped from cart, cartEuros, pointsCart and wishlist arrays.
   * Use before persisting to Firestore so these transient template fields are not stored.
   */
  private stripUiFields(user: any): any {
    const clean = { ...user }
    for (const arr of ['cart', 'cartEuros', 'pointsCart', 'wishlist']) {
      if (clean[arr] && Array.isArray(clean[arr])) {
        clean[arr] = clean[arr].map((item: any) => {
          const { idx, plus, minus, nameLink, nameMsg, ...rest } = item
          return rest
        })
      }
    }
    return clean
  }

  purgeCart(cart: any[]) {
    if (cart instanceof Array && cart.length) {
      for (const line of cart) {
        delete line.idx
        delete line.plus
        delete line.minus
        delete line.nameLink
        delete line.nameMsg
        line.status = OrderStatus.PLACED
      }
    }
    return [...cart]
  }

  confirmReceiveCard(card: any, cardId: string, order: any, user: any = null): Observable<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)

    return new Observable<any>(obs => {
      const valid = true

      if (valid) {
        this.updateCardDetails(card, cardId, order)

        api.post('db/action', {
          action: 'list',
          collection: 'cards',
          where: [['active', '==', true], ['cardId', '==', cardId]]
        }, true).pipe(first()).toPromise().then(existingCards => {
          if (existingCards && existingCards.length) {
            const existCard = existingCards.find(x => x.cardId === cardId)
            if (existCard.email.toLowerCase() !== user.email.toLowerCase()) {
              obs.error(this.transloco.translate('card_registered_other_user'))
              obs.complete()
            } else if (existCard.received) {
              obs.error(this.transloco.translate('card_already_received'))
              obs.complete()
            } else {
              this.createCard(api, card, cardId, user, existCard).then(() => {
                this.updateTransaction(order, card, cardId, obs, user)
              })
            }
          } else if (this.g.program.options.allowExternalCards) {
            this.createCard(api, card, cardId, user).then(() => {
              this.updateTransaction(order, card, cardId, obs, user)
            })
          } else {
            obs.error(this.transloco.translate('card_not_issued', { platform: this.g.program.name }))
            obs.complete()
          }
        })
      } else {
        obs.error(this.transloco.translate('invalid_serial_number'))
        obs.complete()
      }
    })
  }

  private updateCardDetails(card: any, cardId: string, order: any) {
    card.cardId = cardId
    const cart = order.cart
    const index = cart.findIndex(i => i.cardIdTemp === card.cardIdTemp && i.name.toLowerCase().indexOf('emissão') === -1)
    cart[index].cardId = cardId.toString()
    cart[index].cardReceivedIn = format(new Date(), 'yyyy-MM-dd HH:mm')

    const secondLine = cart.findIndex(i => i.cardIdTemp === cart[index].cardIdTemp && !i.cardId)
    if (secondLine >= 0) {
      cart[secondLine].cardId = cardId.toString()
      cart[secondLine].cardReceivedIn = cart[index].cardReceivedIn
    }
  }

  private createCard(api: ApiService, card: any, cardId: string, user: any, existCard?: any): Promise<void> {
    // Use the existing document ID or cardId as fallback for new cards
    const docId = existCard?.id || `${cardId}`.trim()
    return api.post('db/action', {
      action: 'set',
      collection: 'cards',
      nick: this.g.nick,
      id: docId,
      email: this.g.user.email,
      data: {
        active: true,
        cardId: `${cardId}`.trim(),
        createdAt: existCard?.createdAt || new Date().toISOString(),
        delivered: true,
        displayName: user.displayName || 'user',
        email: user.email,
        expirationDate: card.expirationDate,
        id: docId,
        nick: this.g.nick,
        received: card.received,
        receivedIn: new Date().toISOString(),
        seller: card.seller,
      }
    }, true).pipe(first()).toPromise()
  }

  private updateTransaction(order: any, card: any, cardId: string, obs: any, user: any) {
    order.historic = order.historic || []
    order.historic.push({
      createdAt: new Date().toISOString(),
      timestamp: new Date().getTime(),
      updatedBy: this.g.user.email,
      action: this.transloco.translate('card_number_added', { cardId: `<strong>${cardId}</strong>` })
    })

    this.fss.update(`transactions`, order.id, {
      createdAt: order.createdAt,
      cart: order.cart,
      historic: order.historic
    }).then(res => {
      const emailSrv = use<EmailService>(EmailService)
      this.notify.update(this.transloco.translate('card_registered_notification', { cardId, days: card.rechargePeriod }), 'btn-success', 10000)
      emailSrv.sendConfirmationCardLoadEmail(card, user).subscribe({
        next: () => {
          obs.next({ action: 'email' })
          obs.complete()
        },
        error: () => {
          obs.next({ action: 'email' })
          obs.complete()
        }
      })
    }).catch(error => {
      obs.error(error)
      obs.complete()
    })
  }


  sendConfirmationCardLoadEmail(card, cardId, user = null): Observable<any> {
    const _program = {
      name: this.g.program.name,
      sender: this.g.program.sender,
      email: this.g.program.email,
      url: this.g.program.url,
      subject: `Registo do carregamento do ${card.name} nº ${cardId}`,
      html: `
      O carregamento do ${card.name} nº ${cardId} ficou registado na plataforma ${this.g.program.name} e no prazo de ${card.rechargePeriod} dias úteis o carregamento será efectuado.<br><br>
      Caso tenhas alguma dúvida, contacta o <b>serviço de apoio</b>.<br>
      (Disponível de segunda a sexta das 9h30 às 13:00 e das 14:00 às 18h00)<br>`,
      imgHeader: this.g.program.images['emailHeader'],
      imgFooter: this.g.program.images['emailFooter'],
      templateId: this.g.program.emails['generic']
    }

    const _item = {
      displayName: user !== null ? user.displayName : this.g.user.displayName,
      email: user !== null ? user.email : this.g.user.email,
    }

    const emailSrv = use<EmailService>(EmailService)
    return emailSrv.sendOne(_item, _program)
  }

  validateCardCartLine(product: any, cartLine: any, purchaseType: string): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Promise<any>(async (resolve, reject) => {
      if (purchaseType === 'LOAD' && product.seller === 'SIERRA') {

        const theCard = await this.fss.list('cards', { where: [['cardId', '==', cartLine.cardId]] }).pipe(first()).toPromise()

        if (theCard && theCard instanceof Array && theCard.length) {
          resolve(true)
        } else if (!theCard.length || theCard.length === 0) {
          reject('Não é possível carregar esse cartão. Por favor peça um novo cartão.')
        }

      } else resolve(true)
    })
  }

  addCardToCart(product: any, cartLine: any, user: any, purchaseType: string): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Promise<any>(async (resolve, reject) => {
      try {
        await this.validateCardCartLine(product, cartLine, purchaseType)
      } catch (e) {
        return reject(e)
      }

      if (product.variablePrice || product.fixedPrices) {
        cartLine.qty = 1

        if (purchaseType) cartLine.op = purchaseType
        cartLine.points = +cartLine.amtPoints

        if (purchaseType === 'NEW') {
          this.spin.show()
          const cardIdTemp = this.fss.createId

          cartLine.amtPoints = this.g.program.options.chargeCardsToUsers ? parseFloat(cartLine.amtPoints) - product.fee : parseFloat(cartLine.points)
          cartLine.points = this.g.program.options.chargeCardsToUsers ? parseFloat(cartLine.points) - product.fee : parseFloat(cartLine.points)

          await this.add('cart', { ...product, name: `${product.name} - emissão` }, user,
            {
              ...cartLine,
              points: this.g.program.options.chargeCardsToUsers ? product.fee : 0,
              amtPoints: this.g.program.options.chargeCardsToUsers ? product.fee : 0,
              chargeCardsToUsers: this.g.program.options.chargeCardsToUsers || true,
              nonBillable: !this.g.program.options.chargeCardsToUsers || false
            }, cardIdTemp)

          setTimeout(() => {
            this.add('cart', product, user, { ...cartLine, cardId: '' }, cardIdTemp)
            this.spin.hide()
          }, 500)
          resolve(true)
        } else {
          if (cartLine.cardId) user.cardId = cartLine.cardId || ''
          await this.add('cart', product, user, cartLine, cartLine.cardId || '')
          resolve(true)
        }
      } else reject('Este item não é recarregavel')
    })
  }

  /**
   * Checks if a user has enough points to complete the current cart purchase
   * UPDATE: This is the previous version where the points purchase was listed as a cart line
   * @param user
   */
  hasSufficientPoints2(user: any): boolean {
    if (user && user.cart && user.cart.length) {
      const payPoints = user.cart.filter(i => i.id === 'buy-point').length ? user.cart.filter(i => i.id === 'buy-point')[0].qty : 0
      const total = user.cart.filter(i => i.id !== 'buy-point').map(i => i.amtPoints).reduce((acc, val) => acc + val, 0)
      return +user.balance >= (total - payPoints)
    }
    return false
  }

  /**
   * Checks if a user has enough points to complete the current cart purchase
   * @param user
   */
  hasSufficientPoints(user: any): boolean {
    if (user && user.cart && user.cart.length) {
      user.pointsCart = user.pointsCart || []
      const payPoints = user.pointsCart.length ? user.pointsCart.map(i => i.qty).reduce((acc, val) => acc + val, 0) : 0
      const total = user.cart.map(i => i.amtPoints).reduce((acc, val) => acc + val, 0)
      return +user.balance >= (total - payPoints)
    }
    return false
  }
  /**
   * Allocates a total amount across groups proportionally to each group's
   * weight. Returns one value per group, in the same order as the input
   * weights. Rounding residual is assigned to the last group so the sum of
   * the returned values always equals the total (avoids 1-cent mismatches
   * between EasyPay's order.value and the sum of capture.splits[].value).
   *
   * Centralized here so buildSplitOrders() and OrderSummaryComponent's
   * calculateSellerSplits() share the same allocation logic.
   *
   * @param weights Per-group weights (e.g. each seller's gross total)
   * @param total Total amount to allocate (e.g. global payment value)
   * @returns Array of allocated values, same length as weights, summing to total
   */
  allocateProportionally(weights: number[], total: number): number[] {
    const totalWeight = weights.reduce((sum, w) => sum + (Number(w || 0)), 0)
    const result: number[] = []
    let allocated = 0

    for (let i = 0; i < weights.length; i++) {
      const isLast = i === weights.length - 1
      let value = 0
      if (totalWeight > 0 && total > 0) {
        value = Math.round((Number(weights[i] || 0) / totalWeight) * total * 100) / 100
      }
      if (isLast) {
        // Last group absorbs the rounding residual so the sum is exact
        value = Math.round((total - allocated) * 100) / 100
      } else {
        allocated = Math.round((allocated + value) * 100) / 100
      }
      result.push(value)
    }

    console.log('[SPLIT-DEBUG] allocateProportionally', {
      weights,
      total,
      totalWeight,
      result,
      sumResult: result.reduce((s, v) => s + v, 0)
    })

    return result
  }

  /**
   * Calculates cart euros totals including subsidy amount and payment value
   * The subsidy is a MAXIMUM - users can use their available balance up to that limit
   *
   * Rounding policy (Math.ceil on every monetary value, in the exact order used
   * by the backend placeOrderEurosWithTransaction / placeOrderSegmentsWithTransactions):
   *   - orderEuros = ceil(sum(priceEuros * qty) * 100) / 100
   *   - discount   = ceil(orderEuros * subsidyPercent * 100) / 100
   *   - discount   = discount > balance ? balance : discount
   *   - paidValue  = ceil((orderEuros - discount) * 100) / 100
   * This guarantees order.paymentValue (frontend) === order.paidValue (backend),
   * so the EasyPay webhook never reports a value mismatch.
   *
   * @param cartItems Array of cart items with priceEuros and qty properties
   * @param applySubsidy Whether to apply subsidy (toggle state)
   * @param userBalanceEuros User's euros balance (used to calculate effective subsidy)
   * @returns Object containing total, maxSubsidy, effectiveSubsidy, and paymentValue
   */
  calculateCartEurosTotals(
    cartItems: any[],
    applySubsidy: boolean = true,
    userBalanceEuros: number = 0
  ): { total: number, maxSubsidy: number, effectiveSubsidy: number, paymentValue: number } {
    if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
      return { total: 0, maxSubsidy: 0, effectiveSubsidy: 0, paymentValue: 0 };
    }

    // Calculate total as sum of priceEuros * qty for all items, rounded up to cents
    const rawTotal = cartItems
      .map(i => (+(i.priceEuros || 0)) * (+(i.qty || 1)))
      .reduce((acc, val) => acc + val, 0);
    const total = Math.ceil(rawTotal * 100) / 100;

    // Calculate maximum subsidy based on program settings (ceil, backend-aligned)
    const subsidyPercent = applySubsidy ? (this.g.program.maxOrderSubsidy || 0) / 100 : 0;
    const maxSubsidy = Math.ceil(total * subsidyPercent * 100) / 100;

    // Effective subsidy is limited by user's available balance. Matches the
    // backend order: ceil the subsidy first, then cap by balance (no extra
    // rounding after the cap, so the invariant total = subsidy + payment holds).
    const effectiveSubsidy = applySubsidy
      ? (maxSubsidy > userBalanceEuros ? userBalanceEuros : maxSubsidy)
      : 0;

    // Calculate final payment value (ceil, backend-aligned)
    const paymentValue = Math.ceil((total - effectiveSubsidy) * 100) / 100;

    const result = {
      total,
      maxSubsidy,
      effectiveSubsidy,
      paymentValue
    };

    console.log('[SPLIT-DEBUG] calculateCartEurosTotals', {
      cartItems: cartItems.map(i => ({
        name: i.name,
        priceEuros: i.priceEuros,
        qty: i.qty,
        sellerId: i.sellerId,
        lineTotal: (+(i.priceEuros || 0)) * (+(i.qty || 1))
      })),
      applySubsidy,
      userBalanceEuros,
      subsidyPercent: applySubsidy ? (this.g.program.maxOrderSubsidy || 0) / 100 : 0,
      rawTotal,
      total,
      maxSubsidy,
      effectiveSubsidy,
      paymentValue,
      invariant: total === effectiveSubsidy + paymentValue,
      result
    })

    return result;
  }

  /**
   * Checks if user has any euros balance to apply subsidy (partial or full)
   * @param user User object with cartEuros and balanceEuros properties
   * @returns Boolean indicating if user has any balance to use
   */
  hasSufficientEurosBalance(user: any): boolean {
    if (user && user.cartEuros?.length) {
      return +user.balanceEuros > 0;
    }
    return false;
  }

  getApplicablePointsBalance(): number {
    const isPRPM = this.resolveIsPRPM()
    const isPRP = this.resolveIsPRP()
    const segment = this.g.get('segment')
    const segmentBalance = +(segment?.balance || 0)
    const userBalance = +(this.g.user?.balance || 0)

    if (isPRPM && isPRP) return Math.max(userBalance, segmentBalance)
    if (isPRPM) return segmentBalance
    return userBalance
  }

  resolveIsPRPM(): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy
    const prpmSegment = this.g.program?.segmentsWhoCanBuy?.PRPM

    if (!isFeatureEnabled || !prpmSegment) {
      return false
    }

    const prpmSegmentId = prpmSegment.id
    const userPrimarySegmentId = this.g.user?.segment?.id
    const userSegments = this.g.user?.segments || []

    return userPrimarySegmentId === prpmSegmentId || userSegments.includes(prpmSegmentId)
  }

  resolveIsPRP(): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy
    const prpSegment = this.g.program?.segmentsWhoCanBuy?.PRP

    if (!isFeatureEnabled || !prpSegment) {
      return false
    }

    const prpSegmentId = prpSegment.id
    const userPrimarySegmentId = this.g.user?.segment?.id
    const userSegments = this.g.user?.segments || []

    return userPrimarySegmentId === prpSegmentId || userSegments.includes(prpSegmentId)
  }

  resolveIsPREM(): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy
    const premSegment = this.g.program?.segmentsWhoCanBuy?.PREM

    if (!isFeatureEnabled || !premSegment) {
      return false
    }

    const premSegmentId = premSegment.id
    const userPrimarySegmentId = this.g.user?.segment?.id
    const userSegments = this.g.user?.segments || []

    return userPrimarySegmentId === premSegmentId || userSegments.includes(premSegmentId)
  }

  resolveIsPRE(): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy
    const preSegment = this.g.program?.segmentsWhoCanBuy?.PRE

    if (!isFeatureEnabled || !preSegment) {
      return false
    }

    const preSegmentId = preSegment.id
    const userPrimarySegmentId = this.g.user?.segment?.id
    const userSegments = this.g.user?.segments || []

    return userPrimarySegmentId === preSegmentId || userSegments.includes(preSegmentId)
  }

  canBuyWithPoints(): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy

    // If feature is disabled, everyone can buy
    if (!isFeatureEnabled) {
      return true
    }

    const prpSegmentId = this.g.program?.segmentsWhoCanBuy?.PRP?.id
    const userPrimarySegmentId = this.g.user?.segment?.id
    const userSegments = this.g.user?.segments || []

    const hasPRP = userPrimarySegmentId === prpSegmentId || userSegments.includes(prpSegmentId)

    return hasPRP || this.resolveIsPRPM()
  }

  canBuyWithEuros(): boolean {
    const isFeatureEnabled = this.g.program?.options?.walletEuros
    const segmentsWhoCanBuy = this.g.program?.options?.segmentsWhoCanBuy


    if (!isFeatureEnabled) return false
    // If feature is disabled, everyone can buy
    if (isFeatureEnabled && !segmentsWhoCanBuy) return true

    const preSegmentId = this.g.program?.segmentsWhoCanBuy?.PRE?.id
    const userPrimarySegmentId = this.g.user?.segment?.id
    const userSegments = this.g.user?.segments || []

    const hasPRE = userPrimarySegmentId === preSegmentId || userSegments.includes(preSegmentId)
    return hasPRE || this.resolveIsPREM()
  }

  /**
   * Check if user euros balance should be visible
   * If segmentsWhoCanBuy disabled: show if walletEuros enabled
   * If segmentsWhoCanBuy enabled: show if walletEuros + PRE segment
   */
  canShowUserEurosBalance(): boolean {
    if (!this.g.program?.options?.walletEuros) return false
    if (!this.g.program?.options?.segmentsWhoCanBuy) return true
    return this.resolveIsPRE()
  }

  /**
   * Check if segment euros balance should be visible
   * If segmentsWhoCanBuy disabled: show if walletEuros enabled
   * If segmentsWhoCanBuy enabled: show if walletEuros + PREM segment
   */
  canShowSegmentEurosBalance(): boolean {
    if (!this.g.program?.options?.walletEuros) return false
    if (!this.g.program?.options?.segmentsWhoCanBuy) return true
    return this.resolveIsPREM()
  }

  /**
   * Check if segment points balance should be visible
   * If segmentsWhoCanBuy disabled: show if ranking enabled (original behavior)
   * If segmentsWhoCanBuy enabled: show if ranking + PRPM segment
   */
  canShowSegmentPointsBalance(): boolean {
    if (!this.g.program?.options?.ranking) return false
    if (!this.g.program?.options?.segmentsWhoCanBuy) return true
    return this.resolveIsPRPM()
  }

  /**
   * Check if user points balance should be visible
   * If segmentsWhoCanBuy disabled: always show if points enabled
   * If segmentsWhoCanBuy enabled: show if points + PRP segment
   */
  canShowUserPointsBalance(): boolean {
    if (!this.g.program?.options?.points) return false
    if (!this.g.program?.options?.segmentsWhoCanBuy) return true
    return this.resolveIsPRP()
  }

  /**
   * Checks if a segment has enough points to complete the current cart purchase
   * @param user
   */
  segmentHasSufficientPoints(user: any): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy
    if (!isFeatureEnabled) return false

    const segment = this.g.get('segment')
    if (!segment) return false

    if (user && user.cart && user.cart.length) {
      user.pointsCart = user.pointsCart || []
      const payPoints = user.pointsCart.length ? user.pointsCart.map(i => i.qty).reduce((acc, val) => acc + val, 0) : 0
      const total = user.cart.map(i => i.amtPoints).reduce((acc, val) => acc + val, 0)
      return +segment.balance >= (total - payPoints)
    }
    return false
  }

  /**
   * Checks if a segment has enough euros to subsidize the current cartEuros purchase
   * @param user
   */
  segmentHasSufficientEuros(user: any): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy
    if (!isFeatureEnabled) return false

    const segment = this.g.get('segment')
    if (!segment) return false

    if (user && user.cartEuros && user.cartEuros.length) {
      return +segment.balanceEuros > 0
    }
    return false
  }

  /**
   * Checks if the user has enough points (individual or segment if PRPM)
   * PRP - can use individual points
   * PRPM - can use segment points
   * @param user
   */
  hasSufficientPointsOrSegment(user: any): boolean {
    const isFeatureEnabled = this.g.program?.options?.segmentsWhoCanBuy

    // If feature is disabled, use standard individual points check
    if (!isFeatureEnabled) {
      return this.hasSufficientPoints(user)
    }

    const userSegments = user?.segments || []
    const prpId = this.g.program.segmentsWhoCanBuy?.PRP?.id
    const prpmId = this.g.program.segmentsWhoCanBuy?.PRPM?.id

    const hasPRP = userSegments.includes(prpId)
    const hasPRPM = userSegments.includes(prpmId)

    // PRP can use individual points
    if (hasPRP && this.hasSufficientPoints(user)) {
      return true
    }
    // PRPM can use segment points
    if (hasPRPM && this.segmentHasSufficientPoints(user)) {
      return true
    }
    return false
  }

  /**
   * Checks if all products in the cart are of type 'payable'
   * @param user
   */
  hasPayableItems(user: any): boolean {
    let has = true
    for (const item of user.cart)
      if (!item.payable) has = false
    return this.g.type === 'shop' ? true : has
  }

  /**
   * Walks all cart items in an order and converts any string/number date fields
   * inside `sale` and `purchase` objects into JS `Date` instances, so that
   * `serializeTimestampsForApi` can convert them to `{ seconds, nanoseconds }`
   * on the way to the backend. Mutates the order in place.
   */
  normalizeSalePurchaseDates(order: any): void {
    if (!order || !Array.isArray(order.cart)) return
    const dateFields = ['orderDate', 'cancelDate', 'crDate', 'billingDate', 'date', 'invoiceDate']
    for (const item of order.cart) {
      if (!item || typeof item !== 'object') continue
      for (const scope of ['sale', 'purchase']) {
        const obj = item[scope]
        if (!obj || typeof obj !== 'object') continue
        for (const field of dateFields) {
          if (obj[field] != null && !(obj[field] instanceof Date)) {
            const d = new Date(obj[field])
            if (!isNaN(d.getTime())) obj[field] = d
          }
        }
      }
    }
  }

  /**
   * Sends the order to API to save with Firebase transaction resources
   * @param orderId
   * @param order
   * @param user
   * @param payment
   */
  saveOrderTransaction(orderId: string, order: any, user: any): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)

    this.normalizeSalePurchaseDates(order)

    return new Promise<any>((resolve, reject) => {
      this.fss.add(`logs`, order)
      this.spin.show()

      const serializedOrder = serializeTimestampsForApi(order)
      const orderDateIso = order.orderDate ? format(new Date(order.orderDate), 'yyyy-MM-dd') : ''

      if (order.type === 'segments') {
        api.post('transactions/placeOrderSegmentsWithTransactions', {
          orderId,
          order: {
            ...serializedOrder,
            orderDate: orderDateIso,
            contactus: `${location.origin}/${this.g.type}/my-account/contactus`
          },
          nick: this.g.nick,
          segment: order.segment
        }, true).subscribe(result => {
          if (result === true) {
            resolve(true)
            return
          }

          if (result && result.success) {
            resolve(result.success)
            return
          }

          const error = result?.error || result?.message || result?.statusMessage || 'No response from server'
          reject(error)
        }, error => {
          const derivedError = error?.error?.error ||
            error?.error?.message ||
            error?.error ||
            error?.message ||
            'Unknown http error'
          reject(derivedError)
        })
      } else if (order.cartType === 'cartEuros') {
        api.post('transactions/placeOrderEurosWithTransaction', {
          orderId,
          order: {
            ...serializedOrder,
            orderDate: orderDateIso,
            contactus: `${location.origin}/${this.g.type}/my-account/contactus`
          },
          nick: this.g.nick,
          userId: user.id,
          email: user.email.toLowerCase()
        }, true).subscribe(result => {
          if (result.success) resolve(result.success)
          else reject(result.error)
        })
      } else {
        api.post('transactions/placeOrderWithTransaction', {
          orderId,
          order: {
            ...serializedOrder,
            orderDate: orderDateIso,
            contactus: `${location.origin}/${this.g.type}/my-account/contactus`
          },
          nick: this.g.nick,
          userId: user.id,
          email: user.email.toLowerCase()
        }, true).subscribe(result => {
          if (result.success) resolve(result.success)
          else reject(result.error)
        })
      }
    })
  }

  /**
   * Creates a pending order before the user pays via EasyPay. The order is
   * created server-side with `primaryCode: PAYMENT_PENDING`, no DISCOUNT
   * transaction, no stock decrement and `paidSuccess: false`. The EasyPay
   * webhook promotes it to `PURCHASE` (plus `DISCOUNT` and stock) on payment
   * confirmation.
   *
   * @param user The user object with cartEuros, deliveryTo, etc.
   * @param applySubsidy Whether to apply segment/individual euros subsidy.
   * @param useSegmentBalance Whether to use segment euros balance.
   * @returns The orderId to use as EasyPay `order.key`.
   */
  async createAwaitingPaymentOrder(user: any, applySubsidy: boolean, useSegmentBalance: boolean): Promise<string> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)

    // Cancel any active PAYMENT_PENDING order for this user before creating a
    // new one. The endpoint auto-cancels the previous pending (expired if older
    // than 15 min, cancelled otherwise) so a new checkout can always proceed.
    // This supports subsidy toggle changes and retry after a refused payment.
    // Fail-closed: any response that is not an explicit success aborts the
    // checkout. This prevents subsidy duplication if the endpoint errors out
    // (Firestore failure, network) instead of silently letting the order through.
    const checkBody = {
      nick: this.g.nick,
      userId: user.id,
    }
    const checkResult = await firstValueFrom(api.post('transactions/checkPendingPayment', checkBody, true))
    if (checkResult?.success !== true) {
      throw new Error('order_creation_failed')
    }

    const orderId = this.fss.createId
    const order = await this.prepareOrder({ ...user, cartType: 'cartEuros' }, applySubsidy, useSegmentBalance)
    order.id = orderId
    order.paidSuccess = false

    console.log('[SPLIT-DEBUG] createAwaitingPaymentOrder mother order', {
      orderId,
      applySubsidy,
      useSegmentBalance,
      orderEuros: order.orderEuros,
      subsidyAmount: order.subsidyAmount,
      paymentValue: order.paymentValue,
      maxSubsidy: order.maxSubsidy,
      totalBeforeDiscount: order.totalBeforeDiscount,
    })

    if (useSegmentBalance) {
      order.type = 'segments'
    }

    // Build splitOrders: one sub-order per seller. The mother order stays as
    // PAYMENT_PENDING (no sellerId at root). The webhook reads splitOrders[]
    // after payment confirmation and creates one PURCHASE per seller, each
    // with sellerId/sellerEmail at the root.
    order.splitOrders = this.buildSplitOrders(order)

    const serializedOrder = serializeTimestampsForApi(order)
    const orderDateIso = order.orderDate ? format(new Date(order.orderDate), 'yyyy-MM-dd') : ''

    const body: any = {
      orderId,
      order: {
        ...serializedOrder,
        orderDate: orderDateIso,
        contactus: `${location.origin}/${this.g.type}/my-account/contactus`
      },
      nick: this.g.nick,
      userId: user.id,
      email: user.email.toLowerCase(),
      awaitingPayment: true
    }

    if (useSegmentBalance) {
      body.segment = order.segment
      await firstValueFrom(api.post('transactions/placeOrderSegmentsWithTransactions', body, true))
    } else {
      await firstValueFrom(api.post('transactions/placeOrderEurosWithTransaction', body, true))
    }

    return orderId
  }

  /**
   * Builds one sub-order per seller from a prepared cartEuros order.
   * Each sub-order has its own id, sellerId/sellerEmail at the root, and
   * recalculated totals (orderEuros, paidValue, discountEuros, etc.).
   * The total discount and maxSubsidy are split proportionally across
   * sub-orders based on each seller's share of orderEuros. Any rounding
   * residual is assigned to the last sub-order.
   *
   * Used by createAwaitingPaymentOrder so the webhook can later create
   * one PURCHASE transaction per seller.
   */
  private buildSplitOrders(order: any): any[] {
    const cartItems = order.cart || []
    if (!cartItems.length) return []

    // Group cart items by sellerId (items without sellerId go to '_no_seller')
    const sellerGroups = new Map<string, any[]>()
    for (const item of cartItems) {
      const key = item.sellerId || '_no_seller'
      if (!sellerGroups.has(key)) sellerGroups.set(key, [])
      sellerGroups.get(key)!.push(item)
    }

    const totalDiscountEuros = Number(order.subsidyAmount || 0)
    const totalMaxSubsidy = Number(order.maxSubsidy || 0)

    // Per-group gross totals, used as weights for proportional allocation
    const groupItems = Array.from(sellerGroups.values())
    const subOrderEurosList = groupItems.map(items =>
      items.reduce(
        (sum: number, i: any) => sum + (Number(i.priceEuros || 0) * Number(i.qty || 1)),
        0,
      ),
    )

    // Centralized proportional allocation: guarantees the rounded sum of each
    // group's allocated value equals the rounded global total.
    const allocatedDiscounts = this.allocateProportionally(subOrderEurosList, totalDiscountEuros)
    const allocatedMaxSubsidies = this.allocateProportionally(subOrderEurosList, totalMaxSubsidy)

    console.log('[SPLIT-DEBUG] buildSplitOrders inputs', {
      orderId: order.id,
      orderEuros: order.orderEuros,
      subsidyAmount: order.subsidyAmount,
      paymentValue: order.paymentValue,
      maxSubsidy: order.maxSubsidy,
      totalDiscountEuros,
      totalMaxSubsidy,
      sellerGroups: Array.from(sellerGroups.keys()).map(key => ({
        sellerId: key,
        items: sellerGroups.get(key)!.map(i => ({
          name: i.name,
          priceEuros: i.priceEuros,
          qty: i.qty,
          lineTotal: Number(i.priceEuros || 0) * Number(i.qty || 1)
        }))
      })),
      subOrderEurosList,
      allocatedDiscounts,
      allocatedMaxSubsidies,
      sumAllocatedDiscounts: allocatedDiscounts.reduce((s, v) => s + v, 0),
      sumAllocatedMaxSubsidies: allocatedMaxSubsidies.reduce((s, v) => s + v, 0),
    })

    const splitOrders: any[] = []

    for (let index = 0; index < groupItems.length; index++) {
      const items = groupItems[index]
      const subId = this.fss.createId
      const sellerItem = items.find((i: any) => i.sellerId)

      const subOrderEuros = subOrderEurosList[index]
      const subDiscountEuros = allocatedDiscounts[index]
      const subMaxSubsidy = allocatedMaxSubsidies[index]
      const subPaidValue = Math.round((subOrderEuros - subDiscountEuros) * 100) / 100

      console.log('[SPLIT-DEBUG] buildSplitOrders sub-order', {
        index,
        sellerId: sellerItem ? sellerItem.sellerId : null,
        subOrderEuros,
        subDiscountEuros,
        subMaxSubsidy,
        subPaidValue,
      })

      const splitOrder: any = {
        ...order,
        id: subId,
        cart: items,
        sellerId: sellerItem ? sellerItem.sellerId : null,
        sellerEmail: sellerItem ? (sellerItem.sellerEmail || null) : null,
        orderEuros: subOrderEuros,
        orderPoints: subOrderEuros,
        orderQty: items.reduce((total: number, item: any) => total + (item.qty || 1), 0),
        totalBeforeDiscount: subOrderEuros,
        subsidyAmount: subDiscountEuros,
        discountEuros: subDiscountEuros,
        paymentValue: subPaidValue,
        paidValue: subPaidValue,
        maxSubsidy: subMaxSubsidy,
      }

      splitOrders.push(splitOrder)
    }

    console.log('[SPLIT-DEBUG] buildSplitOrders result', {
      count: splitOrders.length,
      sumOrderEuros: splitOrders.reduce((s, o) => s + o.orderEuros, 0),
      sumDiscountEuros: splitOrders.reduce((s, o) => s + o.discountEuros, 0),
      sumPaymentValue: splitOrders.reduce((s, o) => s + o.paymentValue, 0),
      orderPaymentValue: order.paymentValue,
    })

    return splitOrders
  }

  async prepareOrder(user: any, applySubsidy: boolean = true, useSegmentBalance: boolean = null): Promise<any> {
    const d = new Date()
    const isCartEuros = user.cartType === CartType.CART_EUROS

    const _program = {
      email: this.g.program.sender,
      sender: this.g.program.sender,
      imgHeader: this.g.program.images['emailHeader'],
      imgFooter: this.g.program.images['emailFooter'],
      name: this.g.program.name,
      templateId: isCartEuros
        ? (this.g.program.emails['orderConfirmationEuros'] || this.g.program.emails['orderConfirmation'])
        : this.g.program.emails['orderConfirmation'],
      type: this.g.program.type,
      url: this.g.program.url,
      emails: this.g.program.emails
    }

    const cart = isCartEuros
      ? this.parseCartEuros(user)
      : this.parseProductsPromotions(this.purgeCart(user[user.cartType] || []), user)

    const cartItems = Array.from(cart).map((item: any) => ({
      ...item,
      uniqueCartItemNo: this.generateCartItemId(),
      sale: {
        qty: +item.qty || 0,
        price: +item.price || 0,
        vatRate: +item.vatRate || 0,
        amount: (+item.qty || 0) * (+item.price || 0),
        orderDate: d
      }
    }))

    const transportLines = await this.buildTransportLines(cartItems, user, d)
    if (transportLines.length) {
      cartItems.push(...transportLines)
    }

    // Freeze the billing fee metadata on each line's sale at checkout, so the
    // billing listing no longer needs to recompute it from current config
    try {
      if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
      const sellersList: any[] = await firstValueFrom(this.fss.list('sellers'))
      const sellers = (sellersList || []).reduce((acc: Record<string, any>, s: any) => {
        if (s?.seller) acc[s.seller] = s
        return acc
      }, {})
      // Full segment doc (the user-doc copy has no vat rates), so the sale's
      // vatRate is resolved from the segment's configured rates when set
      const segment = user.segment?.id ? await this.fss.toPromise(this.fss.get('segments', user.segment.id)) : null
      applySaleFees(cartItems, sellers, { nick: this.g.nick, handlingFeePerProductFromDate: this.g.program.handlingFeePerProductFromDate, segment, programVatRate: this.g.program.vatRate })
    } catch (e) {
      console.error('prepareOrder: failed to record sale fees', e)
    }

    const deliveryPeriods = cart.map((a) => a.deliveryPeriod)

    const order = {
      active: true,
      cart: cartItems,
      cartType: user.cartType,
      code: isCartEuros ? PrimaryCode.PURCHASE : PrimaryCode.ORDER,
      email: user.email.toLowerCase(),
      user_email: user.email,
      buyer_email: this.g.program?.options?.buyerEmail ? (this.g.get('buyer-email') || null) : null,
      displayName: user.displayName,
      deliveryDate: `${Math.max(...deliveryPeriods)} dias úteis`,
      deliveryTo: user.deliveryTo || {},
      mobile: user.mobile || (user.deliveryTo && user.deliveryTo.mobile),
      nick: this.g.nick,
      orderCartQty: user.orderCartQty,
      orderCartEurosQty: user.orderCartEurosQty,
      orderDate: d.toISOString(),
      orderEuros: user.orderEuros,
      orderPoints: user.orderPoints,
      orderUnit: user.cartType === 'cartEuros' ? 'euros' : 'pontos',
      paidValue: 0,
      pickupIn: user.pickupIn || {},
      primaryCode: isCartEuros ? PrimaryCode.PURCHASE : PrimaryCode.ORDER,
      program: _program,
      purchaseOrderDate: null,
      segment: user.segment || '',
      status: OrderStatus.PLACED,
      store: this.g.get('selectedStore') || null,
      storeId: (this.g.get('selectedStore') || { id: null }).id,
      shipDate: null,
      subject: 'Confirmação da encomenda ',
      uid: user.id,
      userCode: user.userCode || '',
      username: user.email,
      updatedAt: d.toISOString(),
      updatedBy: user.email,
      historic: [
        {
          createdAt: new Date().toISOString(),
          timestamp: new Date().getTime(),
          updatedBy: this.g.user.email,
          action: this.transloco.translate('order_created_with_items', { items: cart.map(c => `${c.qty}x ${c.name} - ${c.points} ${this.transloco.translate('points')}`).join(`<br>`) })
        }
      ]
    }

    if (order.pickupIn && order.pickupIn.pickupTime) {
      order['deliveryDate'] = order.pickupIn.pickupTime
    }

    if (isCartEuros) {
      const segment = this.g.get('segment')
      const shouldUseSegment = useSegmentBalance !== null
        ? useSegmentBalance
        : (this.resolveIsPREM() && segment?.balanceEuros > 0)
      const balanceSource = shouldUseSegment ? (segment.balanceEuros || 0) : (user.balanceEuros || 0)
      const eurosTotals = this.calculateCartEurosTotals(cartItems, applySubsidy, balanceSource)
      order['totalBeforeDiscount'] = eurosTotals.total
      order['subsidyPercent'] = applySubsidy ? (this.g.program.maxOrderSubsidy || 0) : 0
      order['subsidyAmount'] = eurosTotals.effectiveSubsidy
      order['maxSubsidy'] = eurosTotals.maxSubsidy
      order['paymentValue'] = eurosTotals.paymentValue
      order['orderEuros'] = eurosTotals.total
    }

    return deepClonePreservingFieldValues(order)
  }

  async calculateProductShipping(product: any, qty: number, user: any): Promise<any> {
    try {
      if (!this.g.program?.options?.chargeTransportToUsers) return null

      const postcode: string = user?.deliveryTo?.postCode || user?.postCode
      if (!postcode || !product || qty <= 0) return null

      const programNick = this.g.program?.nick
      const overrides = product?.programOverrides?.[programNick]
      if (!overrides?.shippingPolicies) return null

      const cbXrate = this.g.program?.cbXrate || 1
      const normalized = postcode.replace(/[-\s]/g, '').substring(0, 4)
      const regions: Region[] = await this.regionService.getRegionsOnce()

      let matchedRegion: Region | null = null
      for (const regionId of Object.keys(overrides.shippingPolicies)) {
        const region = regions.find(r => r.id === regionId)
        if (!region?.postcodeRanges) continue

        const inRange = region.postcodeRanges.some(range =>
          normalized >= range.from && normalized <= range.to
        )
        if (inRange) { matchedRegion = region; break }
      }
      if (!matchedRegion) return null

      const regionServices = matchedRegion.services || {}
      const defaultEntry = Object.entries(regionServices).find(([, svc]) => svc.active && svc.isDefault)
        || Object.entries(regionServices).find(([, svc]) => svc.active)
      if (!defaultEntry) return null

      const [serviceId, regionService] = defaultEntry
      const productOverride = overrides.shippingPolicies[matchedRegion.id]?.services?.[serviceId]
      const resolvedConfig = this.shipping.resolveRateConfig(regionService, productOverride)
      const costEuros = this.shipping.calculateCost(resolvedConfig, qty, 0, 0)

      if (costEuros <= 0) return null
      const costPoints = Math.ceil(costEuros / cbXrate)

      return {
        name: this.transloco.translate('shipping_line_name', { product: product.name || this.transloco.translate('shipping') }),
        costEuros,
        costPoints,
        region: matchedRegion,
        regionId: matchedRegion.id,
        regionName: matchedRegion.name || matchedRegion.id,
        serviceId,
        serviceName: (regionService as any).name || serviceId,
        vatRate: (regionService as any).vatRate ?? 23,
        deliveryDays: (regionService as any).deliveryDays || 0
      }
    } catch (e) {
      return null
    }
  }

  async calculateShippingLines(cartItems: any[], user: any): Promise<{ name: string, costEuros: number, costPoints: number }[]> {
    try {
      // Only emission products have shipping policies for now
      const emissionLines = cartItems.filter(i => i.name?.toLowerCase().includes('emissão'))
      if (!emissionLines.length) return []

      const programNick = this.g.program?.nick
      const productGroups: Record<string, { product: any, qty: number }> = {}
      for (const item of emissionLines) {
        const overrides = item?.programOverrides?.[programNick]
        if (!overrides?.shippingPolicies) continue
        const productId = item.id || item.productId || item.name
        if (!productGroups[productId]) productGroups[productId] = { product: item, qty: 0 }
        productGroups[productId].qty += item.qty || 1
      }

      const lines: { name: string, costEuros: number, costPoints: number }[] = []
      for (const group of Object.values(productGroups)) {
        const line = await this.calculateProductShipping(group.product, group.qty, user)
        if (line) lines.push({ name: line.name, costEuros: line.costEuros, costPoints: line.costPoints })
      }
      return lines
    } catch (e) {
      return []
    }
  }

  async buildTransportLines(cartItems: any[], user: any, orderDate?: Date): Promise<any[]> {
    try {
      if (!this.g.program?.options?.chargeTransportToUsers) return []

      const postcode: string = user?.deliveryTo?.postCode || user?.postCode
      if (!postcode) return []

      // Only emission products have shipping policies for now
      const emissionLines = cartItems.filter(i => i.name?.toLowerCase().includes('emissão'))
      if (!emissionLines.length) return []

      const programNick = this.g.program?.nick
      const normalized = postcode.replace(/[-\s]/g, '').substring(0, 4)
      const regions: Region[] = await this.regionService.getRegionsOnce()

      const regionGroups: Record<string, { items: any[], region: Region, seller: string, product: any }> = {}

      for (const item of emissionLines) {
        const overrides = item?.programOverrides?.[programNick]
        if (!overrides?.shippingPolicies) continue

        for (const regionId of Object.keys(overrides.shippingPolicies)) {
          const region = regions.find(r => r.id === regionId)
          if (!region?.postcodeRanges) continue

          const inRange = region.postcodeRanges.some(range =>
            normalized >= range.from && normalized <= range.to
          )
          if (!inRange) continue

          const groupKey = `${regionId}__${item.seller}__${item.id || item.productId || item.name}`
          if (!regionGroups[groupKey]) {
            regionGroups[groupKey] = { items: [], region, seller: item.seller, product: item }
          }
          regionGroups[groupKey].items.push(item)
          break
        }
      }

      // One transport line per product (region + seller + product)
      const lines: any[] = []
      for (const group of Object.values(regionGroups)) {
        const line = await this.calculateProductShipping(group.product, group.items.length, user)
        if (!line) continue

        lines.push({
          id: this.generateCartItemId(),
          name: line.name,
          points: line.costPoints,
          amtPoints: line.costPoints,
          price: line.costEuros,
          amtEuros: line.costEuros,
          qty: 1,
          vatRate: line.vatRate,
          isTransport: true,
          serviceName: line.serviceName,
          regionName: line.regionName,
          uniqueCartItemNo: this.generateCartItemId(),
          sale: {
            qty: 1,
            price: line.costEuros,
            vatRate: line.vatRate,
            amount: line.costEuros,
            orderDate: orderDate || new Date()
          }
        })
      }
      return lines
    } catch (e) {
      return []
    }
  }

  generateId(): string {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.createId
  }

  generateCartItemId(): string {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.createId
  }

  placeOrder(order: any): Observable<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Observable<any>(obs => {
      const transaction: ITransaction = {
        ...order,
        code: TransactionType.ORDER,
        primaryCode: TransactionType.ORDER,
        status: OrderStatus.PLACED,
        historic: [
          {
            createdAt: new Date().toISOString(),
            timestamp: new Date().getTime(),
            updatedBy: this.g.user.email,
            action: this.transloco.translate('order_created')
          }
        ]
      }

      this.fss.add(`transactions`, { ...transaction, active: true }).then(doc => {
        if (order.cart && order.cart instanceof Array) {
          for (const item of order.cart) {
            const cartItem: ICartItem = {
              ...item,
              nick: order.nick,
              transactionId: order.transactionId,
              code: transaction.code,
              status: OrderStatus.PLACED
            }
            this.fss.update(`transactionsItems`, cartItem.uniqueCartItemNo, cartItem)
          }
        }
        obs.next(doc)
        obs.complete()
      })
    })
  }

  /**
   * Parses the user's cart looking for products with variants and sets the variants
   * in the name to be displayed in summary tables
   * @param user
   */
  parseProducsVariants(user: any): any[] {
    const cartLines: any[] = []
    if (user && user.cart && user.cart.length) {
      for (const cartItem of user.cart) {
        const line = { ...cartItem }
        if (cartItem.variants) {
          for (const variant in cartItem.variants) {
            if (cartItem.variants[variant] && cartItem.variants[variant] instanceof Array) {
              line.name = line.name + ` (${this.transloco.translate(`cart_summary_variant_${variant}`)}: ${cartItem.variants[variant].join(', ')})`
              line['selectedVariants'] = cartItem.variants[variant].join(', ')
            }
          }
        }
        cartLines.push(line)
      }
    }
    return cartLines
  }

  parseCartEuros(user: any): any[] {
    const cartLines: any[] = []
    if (user && user.cartEuros && user.cartEuros.length) {
      for (const cartItem of user.cartEuros) {
        const line = { ...cartItem }
        cartLines.push(line)
      }
    }
    return cartLines
  }

  /**
   * Parses the user's cart looking for products that have promotional balances
   * making the appropriate balance compensation
   * @param cart
   * @param user
   */
  parseProductsPromotions(cart: any[], user: any = null) {
    const promotions = user && user.promotions ? { ...user.promotions } : null
    if (cart.length && user && promotions && Object.keys(promotions).length) {
      const cartLines: any[] = []
      for (const cartItem of cart) {
        const line = { ...cartItem }
        if (promotions[cartItem.id] && +promotions[cartItem.id].balance > 0) {
          const promo = { ...promotions[cartItem.id] }
          if (+promo.balance >= +line.qty) {
            line.promotionBalance = +line.qty
            promo.balance -= +line.qty
          } else {
            line.promotionBalance = +line.qty - (+promo.balance)
            promo.balance = 0
          }
        }
        cartLines.push(line)
      }
      return cartLines
    } else return [...cart]
  }

  placeGuestUserCart(guest: any): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (this.g.isBrowser && Cry.getLocal('layoutTemp')) guest = Cry.getLocal('layoutTemp')
    const update = {
      cart: guest.cart,
      addresses: [...(guest.addresses || []), ...(this.g.user.addresses || [])],
      deliveryTo: guest.deliveryTo || null,
      pickupIn: guest.pickupIn || null,
      createdAt: this.g.user.createdAt,
      createdBy: this.g.user.createdBy
    }
    return window ? this.fss.update(`users`, this.g.user.id, update).then(() => Cry.removeLocal('layoutTemp')) : Promise.resolve(true)
  }

  /**
   * Compares a pre-refresh snapshot of a cart with the post-refresh array and
   * alerts the user when any line had its `price` or `stock` changed.
   *
   * Lines are matched by composite identity (`id` + `cardIdTemp` + `addedInCart`
   * + `name`) using a per-key FIFO queue, so a removed line does not shift
   * following entries (no index-based false positives) and multiple lines of
   * the same product (digital vouchers without `cardIdTemp`, card + "- emissão"
   * line) are not collapsed into a single map entry. Returns true when a change
   * was detected (the alert is still shown, the checkout is not blocked).
   */
  verifyItemsChange(older: string | any[], newer: any[]): boolean {
    const old = typeof older === 'string' ? JSON.parse(older) : older
    if (!Array.isArray(old) || !Array.isArray(newer)) return false

    // Include addedInCart and name in the key (and store a FIFO list per key)
    // so multiple lines of the same product with different amounts (e.g. digital
    // vouchers without cardIdTemp, or a card and its "- emissão" line) do not
    // overwrite each other in the map and trigger a false price-change alert.
    const lineKey = (l: any) =>
      `${l?.id ?? ''}|${l?.cardIdTemp ?? ''}|${l?.addedInCart ?? ''}|${l?.name ?? ''}`
    const newerByKey = new Map<string, any[]>()
    for (const n of newer) {
      const key = lineKey(n)
      const list = newerByKey.get(key)
      if (list) {
        list.push(n)
      } else {
        newerByKey.set(key, [n])
      }
    }

    let changed = false
    for (const o of old) {
      const list = newerByKey.get(lineKey(o))
      const n = list?.shift()
      if (!n) continue
      if (o.price !== n.price) changed = true
      if (o.stock !== n.stock) changed = true
      if (changed) break
    }

    if (changed) {
      this.notify.alert(
        this.transloco.translate('attention'),
        this.transloco.translate('cart_items_price_stock_changed')
      ).then()
    }
    return changed
  }

  /**
   * Checks if the product already exists with the same characteristics in the user's cart
   * Identical objects do not result in TRUE when compared. An alternative to compare them
   * without using an external library (lodash) is to compare with JSON.stringify, and it works well
   * @param product
   * @param user
   */
  private productIsSame(product: any, user: any, what: string): { isSame: boolean, index: number } {
    let isSame = false
    let index = -1
    const cartType = what

    if (user && user[cartType] && user[cartType] instanceof Array) {
      for (const [idx, prod] of user[cartType].entries()) {

        if (product.variants && prod.variants) {
          if (JSON.stringify(product.variants) === JSON.stringify(prod.variants)) {
            isSame = true
            index = idx
          }
        } else if (product.id === prod.id) {
          isSame = true
          index = idx
        }
      }
    }
    return { isSame, index }
  }

  async updateLine(what: string, sign: string, i: number, user: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (['cart', 'cartEuros'].includes(what)) user = this.changeQtiesCart(sign, i, user, what)
    if (what === 'wishlist') user = this.changeQtiesWish(sign, i, user)
    user = this.calcUserTotals(user)
    this.spin.show()
    await this.fss.update(`users`, user.id, this.stripUiFields(user), { silent: true }).then(() => this.spin.hide()).catch(() => this.spin.hide())
  }

  async updateCart(user) {
    this.spin.show()

    try {
      // Refresh both carts whenever they have lines. Previously this was gated
      // on `user.cartType`, which is only set in `confirmCart` (after the cart
      // page), so on first visit `cartType` was undefined and no FNAC refresh
      // ever ran here. `cartType` is still used downstream for order creation.
      // Both refreshes run in parallel since they are independent of each other.
      const refreshes: Promise<any>[] = []
      if (user.cart && Array.isArray(user.cart) && user.cart.length) refreshes.push(this.updateAllLines(user, CartType.CART))
      if (user.cartEuros && Array.isArray(user.cartEuros) && user.cartEuros.length) refreshes.push(this.updateAllLines(user, CartType.CART_EUROS))
      await Promise.all(refreshes)

      if ((!user.cart || !Array.isArray(user.cart)) && (!user.cartEuros || !Array.isArray(user.cartEuros))) {
        this.spin.hide()
        return user
      }
      /**
       * This TRY handles the case where the SCRAP API fails at some point.
       * If it fails, we assume that, for security, non-rechargeable products
       * are temporarily out of stock
       */
      const api = use<ApiService>(ApiService)
      const clean = this.stripUiFields(user)
      await api.post('db/action', Cry.crypt(JSON.stringify({
        action: 'update',
        collection: 'users',
        id: user.id,
        nick: this.g.nick,
        email: user.email,
        data: {
          createdAt: user.createdAt,
          cart: clean.cart || [],
          cartEuros: clean.cartEuros || []
        }
      }), true)).toPromise()

      return this.calcUserTotals(user)

    } catch (e) {
      if (user.cart && Array.isArray(user.cart)) {
        for (const item of user.cart) {
          if (!item.rechargeable) {
            item.stock = false
          }
        }
      }

      if (user.cartEuros && Array.isArray(user.cartEuros)) {
        for (const item of user.cartEuros) {
          if (!item.rechargeable) {
            item.stock = false
          }
        }
      }

      this.spin.hide()
      throw e
    }
  }

  async updateAllLines(user, cartType: CartType) {
    const results = []
    const cart = cartType === 'cart' ? user.cart : user.cartEuros

    for (const line of cart) {
      if (!line.rechargeable) line.cardId = ''

      const fnac = line.seller === 'FNAC' || line.seller === 'ODISSEIAS'
      results.push(this.updateCartLine(line, this.g.nick, user, cartType, fnac))
    }
    return await Promise.all(results)
  }


  async updateCartLine(line: any, nick: string, user: any, cartType: CartType = CartType.CART, fnac: boolean) {
    if (fnac === true) {
      await this.refreshFnacLine(line, nick, cartType)
    } else if (line.manageStock && cartType === CartType.CART_EUROS) {
      const fss = use<FirestoreService2>(FirestoreService2)
      const productData = await firstValueFrom(fss.get('products', line.id, { nick }))

      if (productData) {
        line.stockQty = productData.stockQty ?? 0
        line.manageStock = productData.manageStock ?? false

        if (line.stockQty > 0) {
          line['nameMsg'] = line['name']
        } else {
          line['nameMsg'] = line['name'] + '<span class="badge btn-danger"><i class="fas fa-times">&nbsp;' + this.transloco.translate('out_of_stock') + '</i></span>'
        }
      }
    } else {
      line['nameMsg'] = line['name']
      line['stock'] = true
    }

    if (cartType === CartType.CART) {
      line.points = parseFloat(line.points || 0)
      line.price = parseFloat(line.price || 0)
      line.amtPoints = line.qty * line.points
    } else if (cartType === CartType.CART_EUROS) {
      line.amtEuros = line.qty * line.price
      line.priceEuros = parseFloat(line.priceEuros || 0)
      line.totalCartEuros = (line.qty * line.priceEuros) - (this.g.program.maxOrderSubsidy / 100) * (line.qty * line.priceEuros)
    }
    return line
  }

  /**
   * Refreshes a FNAC/ODISSEIAS cart line against the FNAC API.
   *
   * Reads `products/{line.id}` first and passes that document to
   * `fnac.updateProduct`, so the call sees a real `sellerCode` and
   * `updatedTimestamp` (the cart line has neither, because `simplifyProduct`
   * strips them). This makes the 24h TTL effective and avoids polluting the
   * `products` document with cart-only fields (`qty`, `amtPoints`, `cardId`,
   * `addedInCart`, ...).
   *
   * The product doc can live under either the program's default nick-scoped
   * path or the raw `g.nick` path (`FirestoreService2.nick()` picks one based
   * on `program.nicks`/`program.nick`/`g.nick`, which do not always agree).
   * `products/show.ts`'s `getID()` already works around this same ambiguity by
   * querying both and preferring whichever resolves to an actual document.
   * Querying only `{ nick }` here missed products stored under the default
   * path, silently treated them as "not found", and marked the line out of
   * stock without ever calling the FNAC API.
   *
   * On any failure the cart never hangs. If the product doc is missing on
   * both paths (deleted/discontinued), the line fails closed (`stock: false`)
   * instead of keeping whatever stale stock it had, so a removed FNAC product
   * cannot stay silently "in stock" in an abandoned cart. `FnacService.updateProduct`
   * never resolves falsy for a truthy input, so no further null-check is
   * needed past this point. Stock/price/points are mapped back to the line;
   * for `cartEuros` the euros-derived totals are recomputed too, which the
   * previous implementation skipped.
   */
  private async refreshFnacLine(line: any, nick: string, cartType: CartType): Promise<void> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const [byDefaultNick, byExplicitNick] = await Promise.all([
      firstValueFrom(this.fss.get('products', line.id)).catch(() => null),
      firstValueFrom(this.fss.get('products', line.id, { nick })).catch(() => null)
    ])
    const productDoc = byDefaultNick && byDefaultNick.id ? byDefaultNick : byExplicitNick
    if (!productDoc) {
      line['stock'] = false
      line['nameMsg'] = line['name'] + '<span class="badge btn-danger"><i class="fas fa-times">&nbsp;' + this.transloco.translate('out_of_stock') + '</i></span>'
      return
    }

    // `force = true`: the cart must verify price/stock against the FNAC API on
    // every visit, bypassing the 24h TTL. This is the moment the user is about
    // to be charged, so a cached document is not good enough (and a document
    // edited by hand keeps its old `updatedTimestamp`, which would otherwise
    // short-circuit the sync and never hit the API).
    const refreshed = await this.fnac.updateProduct(productDoc, true)

    const cbXrate = this.g.program?.cbXrate || 1
    const price = parseFloat(refreshed['price'])
    const stock = !!refreshed['stock']

    line['stock'] = stock
    if (isFinite(price)) {
      line['price'] = price
      line['points'] = Math.ceil(price / cbXrate)
    }

    if (cartType === CartType.CART_EUROS && isFinite(price)) {
      // FNAC reports a single `price` in euros; mirror it into the euros fields
      // so the cart total reflects the current catalogue price.
      line['priceEuros'] = price
    }

    line['nameMsg'] = stock
      ? line['name']
      : line['name'] + '<span class="badge btn-danger"><i class="fas fa-times">&nbsp;' + this.transloco.translate('out_of_stock') + '</i></span>'
  }

  // ═══════════════════════════════════════════════════════════════════
  // V3: Shipping / Region Assignment / Transport Line
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Assign region from user's country + postcode.
   * Priority: cartMeta (persisted) → saved address → fallback.
   * If user already provided postcode in a previous session, reuse it.
   */
  async assignRegion(user: any, programNick: string): Promise<Region | null> {
    const { country, postcode } = this.regionService.resolveShippingLocation(user)

    if (!postcode) {
      this.setPendingTransportLine(user)
      return null
    }

    const regions = await this.regionService.getRegionsOnce()
    const region = this.regionService.findRegion(postcode, country, regions)

    if (!region) {
      this.setPendingTransportLine(user)
      return null
    }

    await this.applyRegionToCart(user, region, programNick)
    return region
  }

  /**
   * Apply region to cart: calculate shipping for each product line.
   */
  async applyRegionToCart(user: any, region: Region, programNick: string): Promise<void> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const cartLines = (user.cart || []).filter((line: any) => !line.isTransport)

    // Collect all products that need shipping
    const productsNeedingShipping = cartLines.filter((line: any) => {
      const override = line.programOverrides?.[programNick]
      return override?.chargeTransportToUsers === true
    })

    if (productsNeedingShipping.length === 0) {
      // No products charge transport to user — remove any existing transport line
      this.removeTransportLine(user)
      return
    }

    // Group products by seller — each seller is a separate physical shipment
    const sellerGroups: Record<string, any[]> = {}
    for (const p of productsNeedingShipping) {
      const key = p.seller || '__unknown'
      if (!sellerGroups[key]) sellerGroups[key] = []
      sellerGroups[key].push(p)
    }

    // Calculate cost per seller group and sum
    let totalCost = 0
    let selectedService: ResolvedService | null = null
    const selectedServiceId = user.cartMeta?.selectedServices?.[region.id]

    for (const sellerProducts of Object.values(sellerGroups)) {
      const firstProduct = sellerProducts[0]
      const groupWeight = sellerProducts.reduce((sum: number, p: any) => sum + (p.weight ?? 0) * (p.qty || 1), 0)
      const groupVolume = sellerProducts.reduce((sum: number, p: any) => sum + (p.volume ?? 0) * (p.qty || 1), 0)

      const availableServices = this.shipping.getAvailableServices(
        firstProduct, region, programNick, groupWeight, groupVolume
      )
      if (availableServices.length === 0) continue

      const svc = this.shipping.selectService(availableServices, selectedServiceId)
      if (!svc) continue

      if (!selectedService) selectedService = svc

      // Re-calculate cost with the actual count for this seller's group
      const resolvedConfig = this.shipping.resolveRateConfig(
        svc,
        firstProduct.programOverrides?.[programNick]?.shippingPolicies?.[region.id]?.services?.[svc.serviceId]
      )
      totalCost += this.shipping.calculateCost(resolvedConfig, sellerProducts.length, groupWeight, groupVolume)
    }

    if (!selectedService || totalCost === 0) {
      this.setTransportLine(user, {
        name: this.transloco.translate('shipping_region', { region: region.name }),
        points: 0,
        amtPoints: 0,
        qty: 1,
        isTransport: true as const,
        nonBillable: true,
        vatRate: 0,
        isPendingAddress: false,
        unavailable: true,
        regionId: region.id,
        regionName: region.name
      })
      return
    }

    const firstProduct = productsNeedingShipping[0]
    const pointsCost = Math.ceil(totalCost / (this.g.program.cbXrate || 1))

    this.setTransportLine(user, {
      name: this.transloco.translate('shipping_region_service', { region: region.name, service: selectedService.name }),
      points: pointsCost,
      amtPoints: pointsCost,
      qty: 1,
      isTransport: true as const,
      nonBillable: true,  // user pays → not in billing list
      vatRate: firstProduct.vatRate || 0,
      regionId: region.id,
      regionName: region.name,
      serviceId: selectedService.serviceId,
      serviceName: selectedService.name,
      deliveryDays: selectedService.deliveryDays,
      shippingCostEuros: totalCost
    })
  }

  /**
   * Set the transport line in the user's cart (replaces existing if any).
   */
  setTransportLine(user: any, line: TransportLine): void {
    if (!user.cart) user.cart = []
    // Remove existing transport line
    user.cart = user.cart.filter((l: any) => !l.isTransport)
    // Add new transport line
    user.cart.push({ ...line, addedInCart: new Date().toISOString() })
  }

  /**
   * Set pending transport line (no postcode / no region match).
   */
  setPendingTransportLine(user: any): void {
    this.setTransportLine(user, {
      name: this.transloco.translate('shipping_pending'),
      points: 0,
      amtPoints: 0,
      qty: 1,
      isTransport: true as const,
      nonBillable: true,
      vatRate: 0,
      isPendingAddress: true
    })
  }

  /**
   * Remove transport line from cart.
   */
  removeTransportLine(user: any): void {
    if (user.cart) {
      user.cart = user.cart.filter((l: any) => !l.isTransport)
    }
  }

  /**
   * Update user's cartMeta (shipping country + postcode) and reassign region.
   */
  async updateShippingLocation(user: any, country: string, postcode: string, programNick: string): Promise<Region | null> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)

    user.cartMeta = {
      ...user.cartMeta,
      shippingCountry: country || 'PT',
      shippingPostcode: postcode
    }

    const region = await this.assignRegion(user, programNick)

    // Persist cartMeta to Firestore
    await this.fss.update('users', user.id, { cartMeta: user.cartMeta }, { silent: true })

    return region
  }

  /**
   * Handle address change during checkout: reassign region, reapply shipping.
   */
  async onAddressChanged(user: any, newAddress: any, programNick: string): Promise<Region | null> {
    return this.updateShippingLocation(
      user,
      newAddress.country,
      newAddress.postalCode,
      programNick
    )
  }

  /**
   * Get transport badge text for product grid display.
   * Returns null if chargeTransportToUsers is false (company pays).
   */
  getTransportBadge(product: any, programNick: string, region: Region | null): string | null {
    const override = product.programOverrides?.[programNick]
    if (!override?.chargeTransportToUsers) return null

    if (!region) return this.transloco.translate('shipping_to_be_defined')

    const availableServices = this.shipping.getAvailableServices(
      product, region, programNick, product.weight ?? 0, product.volume ?? 0
    )
    const defaultService = this.shipping.selectService(availableServices)

    if (!defaultService) return this.transloco.translate('shipping_unavailable')

    const points = Math.ceil(defaultService.resolvedCost / (this.g.program.cbXrate || 1))
    return this.transloco.translate('shipping_points', { points })
  }

  /**
   * Check if checkout should be blocked due to shipping issues.
   */
  isCheckoutBlocked(user: any): { blocked: boolean; reason: string } {
    const transportLine = (user.cart || []).find((l: any) => l.isTransport)

    if (!transportLine) return { blocked: false, reason: '' }

    if (transportLine.isPendingAddress) {
      return { blocked: true, reason: this.transloco.translate('enter_postcode_to_calculate_shipping') }
    }

    if (transportLine.unavailable) {
      return {
        blocked: true,
        reason: this.transloco.translate('seller_does_not_deliver_to_region', { region: transportLine.regionName || this.transloco.translate('selected') })
      }
    }

    return { blocked: false, reason: '' }
  }

  async revalidateStock(cartEuros: any[]): Promise<{ valid: boolean, invalidItems: string[] }> {
    const invalidItems: string[] = []

    for (const item of cartEuros) {
      if (item.manageStock) {
        const fss = use<FirestoreService2>(FirestoreService2)
        const productData = await firstValueFrom(fss.get('products', item.id, { nick: this.g.nick }))

        if (productData) {
          const currentStockQty = productData.stockQty ?? 0

          if (item.qty > currentStockQty) {
            invalidItems.push(item.name)
          }
        }
      }
    }

    return {
      valid: invalidItems.length === 0,
      invalidItems
    }
  }

  bootstrapSaleMap(cartItem: any, orderDate?: any): any {
    if (cartItem.sale) return cartItem
    return {
      ...cartItem,
      sale: {
        qty: +cartItem.qty || 0,
        price: +cartItem.price || 0,
        vatRate: +cartItem.vatRate || 0,
        amount: (+cartItem.qty || 0) * (+cartItem.price || 0),
        orderDate: new Date(orderDate || cartItem.orderDate),
        cancelDate: cartItem.cancelDate ? new Date(cartItem.cancelDate) : null
      }
    }
  }

  bootstrapPurchaseMap(cartItem: any): any {
    if (cartItem.purchase) return cartItem
    return {
      ...cartItem,
      purchase: {
        qty: +cartItem.purchaseQty || +cartItem.qty || 0,
        price: +cartItem.purchasePrice || +cartItem.price || 0,
        vatRate: +cartItem.vatRate || 0,
        amount: (+cartItem.purchaseQty || +cartItem.qty || 0) * (+cartItem.purchasePrice || +cartItem.price || 0),
        date: cartItem.purchaseOrderDate ? new Date(cartItem.purchaseOrderDate) : null,
        cancelDate: cartItem.cancelDate ? new Date(cartItem.cancelDate) : null
      }
    }
  }

}
